import type { CityConfig } from "@/pages/city-template/types";

export const bridgeportConfig: CityConfig = {
  city: "Bridgeport",
  state: "Connecticut",
  stateAbbr: "CT",
  slug: "bridgeport",
  route: "/cities/bridgeport",
  title: "Expedited Passport Services in Bridgeport, CT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Bridgeport, CT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered State Department courier serving Bridgeport and all of Connecticut.",
  h1: "Expedited Passport Services in Bridgeport, CT",
  heroSubtitle:
    "Bridgeport's diverse community and coastal location make it a hub for international travel. Fast Passport Center delivers emergency and expedited passports as fast as 24 hours for Bridgeport travelers.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Scenic%20waterfront%20view%20of%20Bridgeport%20Connecticut%20featuring%20the%20harbor%20and%20downtown%20buildings%2C%20warm%20sunset%20lighting%20over%20Long%20Island%20Sound%2C%20industrial%20heritage%20mixed%20with%20modern%20development%2C%20professional%20travel%20destination%20photography%2C%20clean%20composition%20with%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=ct-bridge-hero-001&orientation=landscape",
  heroImageAlt: "Bridgeport Connecticut waterfront and downtown at sunset",
  geoPosition: "41.1670; -73.2048",
  geoPlacename: "Bridgeport, Connecticut, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~60 miles from Bridgeport",
  },
  nearbyCitiesZips: [
    { name: "Bridgeport", zips: "06601, 06602, 06604, 06605, 06606, 06607, 06608, 06610, 06650, 06673, 06699" },
    { name: "Fairfield", zips: "06824, 06825, 06828" },
    { name: "Stratford", zips: "06614, 06615" },
    { name: "Trumbull", zips: "06611" },
    { name: "Shelton", zips: "06484" },
    { name: "Monroe", zips: "06468" },
    { name: "Easton", zips: "06612" },
    { name: "Milford", zips: "06460, 06461" },
    { name: "Orange", zips: "06477" },
    { name: "West Haven", zips: "06516" },
    { name: "New Haven", zips: "06501-06540" },
    { name: "Ansonia", zips: "06401" },
    { name: "Derby", zips: "06418" },
    { name: "Seymour", zips: "06483" },
    { name: "Naugatuck", zips: "06770" },
    { name: "Waterbury", zips: "06701-06726" },
    { name: "Stamford", zips: "06901-06927" },
    { name: "Norwalk", zips: "06850-06860" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Bridgeport travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving All of Bridgeport",
      desc: "Downtown, North End, East Side, West Side, Black Rock — we cover every Bridgeport neighborhood.",
    },
  ],
  testimonials: [
    {
      name: "Maria G.",
      location: "Bridgeport, CT",
      rating: 5,
      text: "I needed my passport renewed for a family trip to the Dominican Republic. Fast Passport Center had it done in 5 days. Great service for Bridgeport residents.",
      service: "Passport Renewal",
    },
    {
      name: "Carlos R.",
      location: "Stratford, CT",
      rating: 5,
      text: "Emergency passport for business travel to Mexico. Fast Passport Center processed it in 3 days. I highly recommend them to anyone in the Bridgeport area.",
      service: "Emergency Passport",
    },
    {
      name: "Angela T.",
      location: "Fairfield, CT",
      rating: 5,
      text: "Applied for my daughter's first passport. Fast Passport Center explained everything about the parental consent process and got it done in under a week.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "bridge-1",
      name: "Bridgeport Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "120 Middle Street",
      city: "Bridgeport",
      state: "CT",
      zip: "06604",
      phone: "203-333-3235",
      hours: "Mon-Fri 9:00 AM - 4:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Bridgeport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "bridge-2",
      name: "Bridgeport City Hall",
      type: "City Clerk / Passport Services",
      address: "45 Lyon Terrace",
      city: "Bridgeport",
      state: "CT",
      zip: "06604",
      phone: "203-576-7208",
      hours: "Mon-Fri 8:30 AM - 4:30 PM",
      distance: "Downtown Bridgeport",
      website: "https://www.bridgeportct.gov/",
      websiteLabel: "City of Bridgeport",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "bridge-3",
      name: "Fairfield Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "2200 Post Road",
      city: "Fairfield",
      state: "CT",
      zip: "06824",
      phone: "203-259-8569",
      hours: "Mon-Fri 9:00 AM - 4:00 PM",
      distance: "Fairfield",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment recommended for passport applications.",
      icon: "ri-file-shield-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Bridgeport, CT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Where is the closest passport agency to Bridgeport?",
      a: "The New York Passport Agency is approximately 60 miles south of Bridgeport, accessible via I-95. However, if you use Fast Passport Center, you don't need to visit the agency at all. We handle the agency submission for you as a registered courier.",
    },
    {
      q: "Where can I get passport photos in Bridgeport?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Bridgeport, Fairfield, and Stratford. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Bridgeport?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Bridgeport?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Bridgeport?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Bridgeport. We serve Bridgeport and all of Connecticut through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Bridgeport", address: " various CVS locations in Bridgeport", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Bridgeport", address: "various Walgreens locations in Bridgeport", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Bridgeport", address: "various UPS Store locations in Bridgeport", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Bridgeport", address: "various FedEx Office locations in Bridgeport", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Bridgeport", address: "various Walmart locations in Bridgeport", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "CT DMV — Bridgeport",
      address: "95 Freight Street, Bridgeport, CT 06610",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for Bridgeport and Fairfield County residents. Appointments strongly recommended.",
      featured: true,
    },
    {
      name: "CT DMV — Norwalk",
      address: "540 Main Avenue, Norwalk, CT 06851",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "License renewals and REAL ID applications for shoreline residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "HPN",
      name: "Westchester County Airport",
      distance: "~25 miles from Bridgeport",
      address: "240 Airport Rd, White Plains, NY 10604",
      note: "The closest airport to Bridgeport with domestic flights and some international connections. Popular for Fairfield County travelers.",
      flightUrl: "https://www.airport.westchestergov.com/",
      flightLabel: "Westchester County Airport",
      featured: true,
    },
    {
      code: "JFK",
      name: "JFK International Airport",
      distance: "~55 miles from Bridgeport",
      address: "JFK Access Rd, Queens, NY 11430",
      note: "Major international hub with flights to virtually every global destination. Accessible via I-95 south from Bridgeport.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK International",
      featured: false,
    },
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~65 miles from Bridgeport",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's major airport with domestic and international flights. Accessible via I-95 north and I-91 north.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Bridgeport travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Bridgeport residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Bridgeport residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Bridgeport residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Bridgeport, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Bridgeport residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Bridgeport travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Bridgeport residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Bridgeport residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Bridgeport residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Bridgeport." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Bridgeport." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Bridgeport travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Bridgeport travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Bridgeport-area team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Bridgeport address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: [
        "Mail-in application",
        "No expedited handling",
        "No dedicated agent",
        "No phone or email contact for your case",
        "Updates only by physical mail letter",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: [
        "For documented life-or-death emergencies only",
        "Requires proof of emergency travel",
        "Strictly for documented life-or-death emergencies",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Hartford", route: "/cities/hartford" },
    { name: "New Haven", route: "/cities/new-haven" },
    { name: "Stamford", route: "/cities/stamford" },
    { name: "Bridgeport", route: "/cities/bridgeport" },
    { name: "Waterbury", route: "/cities/waterbury" },
    { name: "Greenwich", route: "/cities/greenwich" },
  ],
  fpcOfficeNote:
    "Fast Passport Center serves Bridgeport and all of Connecticut as a remote courier service. We do not have a physical office in Bridgeport, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Remote courier service — Bridgeport, CT area",
  serviceAreaIntro:
    "Fast Passport Center provides expedited passport services to all Bridgeport neighborhoods and surrounding Fairfield County and New Haven County communities. From downtown Bridgeport to the North End, East Side to Black Rock — we serve every Bridgeport neighborhood with the same fast, reliable processing.",
  nearbyCitiesIntro:
    "We proudly serve Bridgeport and all surrounding Connecticut communities. Whether you're in the city center or a nearby suburb, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro:
    "Our Bridgeport customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro:
    "These are the most common mistakes Bridgeport travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro:
    "Bridgeport and Connecticut travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro:
    "Common questions about expedited passport services in Bridgeport, CT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/connecticut",
  statePageName: "Connecticut Passport Services",
  dmvWebsite: "https://portal.ct.gov/DMV",
  dmvLabel: "Connecticut DMV — Official Website",
};

export default bridgeportConfig;